import React, { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { makePointer } from '../../../api/_utils'
import UserFormField from '../UserFormField'
import { useFormContextUser } from '../utils/_utils'
import { computeOptions } from '../utils/computeData'
import { useUserFormContext } from '../utils/userFormContext'
import { useGetIdsReferencesProfessions } from '@toktokdoctech/tokapi-client'
import { specialitiesFieldsToReset, resetRelatedFields } from './_utils'

const Profession = () => {
    const { state } = useUserFormContext()
    const { control, setValue, watch, trigger } = useFormContextUser()

    const errorMessage = 'La profession ne correspond pas à la qualification professionnelle'

    const watched = {
        validRpps: watch('validRpps'),
        profession: watch('profession'),
        qualification: watch('qualification'),
        qualificationId: watch('qualificationId'),
    }

    const { data } = useGetIdsReferencesProfessions(
        {
            qualificationId: watched.qualificationId,
        },
        {
            query: {
                enabled: Boolean(watched.qualificationId),
            },
        },
    )

    const relatedProfessions = React.useMemo(() => computeOptions(data ?? []), [data, watched.qualificationId])

    useEffect(() => {
        if (watched.qualification && watched.profession) {
            trigger(['profession', 'professionPointer'])
        }
    }, [watched.qualification, trigger, watched.profession, relatedProfessions])

    const handleChange = (newProfession: string) => {
        const selectedProfession = data?.find((el) => el?.name === newProfession)

        const professionId = selectedProfession?.id ?? ''
        const professionName = selectedProfession?.name ?? ''

        const professionPointer = makePointer('Profession', professionId)

        setValue('professionPointer', professionPointer)
        setValue('profession', professionName)
        setValue('professionId', professionId)

        resetRelatedFields(specialitiesFieldsToReset, setValue)
    }

    return (
        <Controller
            control={control}
            name="profession"
            rules={{
                required: "Cette information est obligatoire et n'a pas été renseignée",
                validate: (val) => {
                    if (!val) return false
                    if (relatedProfessions.length === 0) return true
                    const matchesQualification = relatedProfessions.some((p) => p.value === val)
                    if (watched.qualificationId && !matchesQualification) {
                        return errorMessage
                    }
                    return true
                },
            }}
            render={({ field, fieldState: { error } }) => (
                <UserFormField
                    variant="select"
                    label="Profession"
                    type="default"
                    name={field.name}
                    status={{
                        error,
                        valid: Boolean(field.value) || (!field.value && watched.validRpps),
                    }}
                    required
                    locked={state.rppsLocked}
                    readonly={state.rppsLocked}
                    disabled={!watched.qualification}
                    placeholder="Profession"
                    value={field.value || undefined}
                    onChange={(e) => {
                        if (e) {
                            handleChange(e)
                        }
                    }}
                    options={relatedProfessions}
                    dataCyScope="UserField"
                />
            )}
        />
    )
}

export default Profession
